import type { ChangeVisual } from '@amzn/awscat-editor';
import { Content } from '@amzn/awscat-pattern-library-service-model';
import { log } from '../loggers/Logger';
import { collectDocIds, RichtextDocId, stringifyDocId } from '../model/RichtextDocId';
import { ContentEditorService } from './ContentEditorService';
import { listEpics } from './EpicOperations';
import { SpecialSectionId } from './SectionOperations';
import { TypedEmitter, Unsubscribe } from './TypedEmitter';

/** A location in the page containing an AI agent scan finding that the user can navigate to. */
export type NavigationItem =
  | {
      /** A specific range of text within a rich text document that can be navigated to. */
      readonly type: 'doc_inline_edit';
      readonly documentId: RichtextDocId;
      readonly inlineEditId: string;
      readonly change: ChangeVisual;
    }
  | {
      /** An inline comment within a rich text document that can be navigated to. */
      readonly type: 'doc_inline_comment';
      readonly documentId: RichtextDocId;
      readonly commentId: string;
    }
  | {
      /** Navigate to the container element holding the epic title, not the rich text field itself. */
      readonly type: 'epic_title_container';
      readonly epicId: string;
    }
  | {
      /** Navigate to the container element holding the epic description, not the rich text field itself. */
      readonly type: 'epic_description_container';
      readonly epicId: string;
    }
  | {
      /** Navigate to the container element holding the task title, not the rich text field itself. */
      readonly type: 'task_title_container';
      readonly taskId: string;
      readonly epicId: string;
    }
  | {
      /** Navigate to the container element holding the task description, not the rich text field itself. */
      readonly type: 'task_description_container';
      readonly taskId: string;
      readonly epicId: string;
    };

/** Lightweight identifier for locating a NavigationItem without carrying its full payload. */
export type NavigationItemKey =
  | {
      /** Key for a specific change within an inline edit in a rich text document. */
      readonly type: 'doc_inline_edit';
      readonly inlineEditId: string;
      readonly changeId: number;
    }
  | {
      /** Key for an inline comment in a rich text document. */
      readonly type: 'doc_inline_comment';
      readonly commentId: string;
    }
  | {
      /** Key for the container element holding the epic title. */
      readonly type: 'epic_title_container';
      readonly epicId: string;
    }
  | {
      /** Key for the container element holding the epic description. */
      readonly type: 'epic_description_container';
      readonly epicId: string;
    }
  | {
      /** Key for the container element holding the task title. */
      readonly type: 'task_title_container';
      readonly taskId: string;
    }
  | {
      /** Key for the container element holding the task description. */
      readonly type: 'task_description_container';
      readonly taskId: string;
    };

/** One inline edit and the individual changes it produced in the editor. */
export interface SectionEditChanges {
  readonly inlineEditId: string;
  readonly changes: readonly ChangeVisual[];
}

/** One inline comment that can be navigated to. */
export interface SectionCommentEntry {
  readonly commentId: string;
  /** Opaque position value used only for interleaving with inline edits in document order. */
  readonly sortingOrder: number;
}

/** Declares that navigable AI suggestions exist within a view. */
export type NavigationReport =
  | {
      /** Report inline edit changes within a rich text document. */
      readonly type: 'doc_inline_edit';
      readonly documentId: RichtextDocId;
      readonly changes: readonly SectionEditChanges[];
    }
  | {
      /** Report inline comments within a rich text document. */
      readonly type: 'doc_inline_comment';
      readonly documentId: RichtextDocId;
      readonly comments: readonly SectionCommentEntry[];
    }
  | {
      /** Report that the epic title container has navigable suggestions. */
      readonly type: 'epic_title_container';
      readonly epicId: string;
    }
  | {
      /** Report that the epic description container has navigable suggestions. */
      readonly type: 'epic_description_container';
      readonly epicId: string;
    }
  | {
      /** Report that the task title container has navigable suggestions. */
      readonly type: 'task_title_container';
      readonly taskId: string;
      readonly epicId: string;
    }
  | {
      /** Report that the task description container has navigable suggestions. */
      readonly type: 'task_description_container';
      readonly taskId: string;
      readonly epicId: string;
    };

/** Declares that all navigation items for a view have been removed, typically when the view unmounts. */
export type NavigationClearReport =
  | {
      readonly type: 'doc_inline_edit';
      readonly documentId: RichtextDocId;
    }
  | {
      readonly type: 'doc_inline_comment';
      readonly documentId: RichtextDocId;
    }
  | { readonly type: 'epic_title_container'; readonly epicId: string }
  | { readonly type: 'epic_description_container'; readonly epicId: string }
  | { readonly type: 'task_title_container'; readonly taskId: string }
  | { readonly type: 'task_description_container'; readonly taskId: string };

export interface NavigationItemChangedPayload {
  readonly item: NavigationItem;
}

/** Extracts the lightweight identity key from a full navigation item. */
export function toKey(item: NavigationItem): NavigationItemKey {
  switch (item.type) {
    case 'doc_inline_edit':
      return { type: 'doc_inline_edit', inlineEditId: item.inlineEditId, changeId: item.change.id };
    case 'doc_inline_comment':
      return { type: 'doc_inline_comment', commentId: item.commentId };
    case 'epic_title_container':
      return { type: 'epic_title_container', epicId: item.epicId };
    case 'epic_description_container':
      return { type: 'epic_description_container', epicId: item.epicId };
    case 'task_title_container':
      return { type: 'task_title_container', taskId: item.taskId };
    case 'task_description_container':
      return { type: 'task_description_container', taskId: item.taskId };
  }
}

/** Returns true when two navigation item keys identify the same location. */
export function keysEqual(a: NavigationItemKey, b: NavigationItemKey): boolean {
  if (a.type !== b.type) return false;
  switch (a.type) {
    case 'doc_inline_edit':
      return (
        b.type === 'doc_inline_edit' &&
        a.inlineEditId === b.inlineEditId &&
        a.changeId === b.changeId
      );
    case 'doc_inline_comment':
      return b.type === 'doc_inline_comment' && a.commentId === b.commentId;
    case 'epic_title_container':
      return b.type === 'epic_title_container' && a.epicId === b.epicId;
    case 'epic_description_container':
      return b.type === 'epic_description_container' && a.epicId === b.epicId;
    case 'task_title_container':
      return b.type === 'task_title_container' && a.taskId === b.taskId;
    case 'task_description_container':
      return b.type === 'task_description_container' && a.taskId === b.taskId;
  }
}

/** Returns true when a navigation item matches a given key. */
export function matchesKey(item: NavigationItem, key: NavigationItemKey): boolean {
  return keysEqual(toKey(item), key);
}

type SuggestionNavigationEvents = {
  /** Fired when the user navigates to a different suggestion via moveNext, movePrev, or moveInto. */
  navigationItemChanged: (payload: NavigationItemChangedPayload) => void;
  /** Fired when the navigation item list is rebuilt or the current position shifts, affecting hasNext/hasPrev. */
  navigationStateReset: () => void;
};

interface SectionReport {
  readonly documentId: RichtextDocId;
  readonly changes: readonly SectionEditChanges[];
}

interface CommentReport {
  readonly documentId: RichtextDocId;
  readonly comments: readonly SectionCommentEntry[];
}

/** Enables next/prev/direct navigation across all AI suggestion locations in document order. */
export class SuggestionNavigationService {
  private static readonly REBUILD_THROTTLE_MS = 5;

  private readonly _emitter = new TypedEmitter<SuggestionNavigationEvents>();
  private readonly _contentEditorService: ContentEditorService;
  private readonly _unsubscribeContentChanged: Unsubscribe;
  // Keyed by stringified RichtextDocId.
  private _sectionReports: Map<string, SectionReport> = new Map();
  // Keyed by stringified RichtextDocId.
  private _commentReports: Map<string, CommentReport> = new Map();
  // Tracks which epic containers have been reported. Keyed as `${epicId}:title` or `${epicId}:description`.
  private _epicContainerReports: Set<string> = new Set();
  // Tracks which task containers have been reported. Keyed as `${taskId}:title` or `${taskId}:description`.
  private _taskContainerReports: Set<string> = new Set();
  private _items: readonly NavigationItem[] = [];
  private _currentKey: NavigationItemKey | undefined = undefined;
  private _lastKnownIndex: number = -1;
  private _rebuildTimer: ReturnType<typeof setTimeout> | undefined = undefined;

  constructor(contentEditorService: ContentEditorService) {
    this._contentEditorService = contentEditorService;
    this._unsubscribeContentChanged = contentEditorService.on('changed', () =>
      this.scheduleRebuild(),
    );
  }

  dispose(): void {
    this._unsubscribeContentChanged();
    if (this._rebuildTimer !== undefined) {
      clearTimeout(this._rebuildTimer);
    }
  }

  report(report: NavigationReport): void {
    switch (report.type) {
      case 'doc_inline_edit':
        this._sectionReports.set(stringifyDocId(report.documentId), {
          documentId: report.documentId,
          changes: report.changes,
        });
        break;
      case 'doc_inline_comment':
        this._commentReports.set(stringifyDocId(report.documentId), {
          documentId: report.documentId,
          comments: report.comments,
        });
        break;
      case 'epic_title_container':
        this._epicContainerReports.add(`${report.epicId}:title`);
        break;
      case 'epic_description_container':
        this._epicContainerReports.add(`${report.epicId}:description`);
        break;
      case 'task_title_container':
        this._taskContainerReports.add(`${report.taskId}:title`);
        break;
      case 'task_description_container':
        this._taskContainerReports.add(`${report.taskId}:description`);
        break;
    }

    // This method is called quite frequent, but rebuilding navigation is
    // expensive, so throttle it
    this.scheduleRebuild();
  }

  clear(report: NavigationClearReport): void {
    switch (report.type) {
      case 'doc_inline_edit':
        this._sectionReports.delete(stringifyDocId(report.documentId));
        break;
      case 'doc_inline_comment':
        this._commentReports.delete(stringifyDocId(report.documentId));
        break;
      case 'epic_title_container':
        this._epicContainerReports.delete(`${report.epicId}:title`);
        break;
      case 'epic_description_container':
        this._epicContainerReports.delete(`${report.epicId}:description`);
        break;
      case 'task_title_container':
        this._taskContainerReports.delete(`${report.taskId}:title`);
        break;
      case 'task_description_container':
        this._taskContainerReports.delete(`${report.taskId}:description`);
        break;
    }
    this.scheduleRebuild();
  }

  private get _content(): Content | undefined {
    return this._contentEditorService.content;
  }

  get items(): readonly NavigationItem[] {
    return this._items;
  }

  /** Returns the inline-edit IDs reported by the specified rendered editor documents. */
  listInlineEditIds(documentIds: readonly RichtextDocId[]): readonly string[] {
    const ids = new Set<string>();
    for (const documentId of documentIds) {
      const report = this._sectionReports.get(stringifyDocId(documentId));
      for (const edit of report?.changes ?? []) {
        ids.add(edit.inlineEditId);
      }
    }
    return Array.from(ids);
  }

  get current(): NavigationItem | undefined {
    return this._items[this.resolveCurrentIndex()];
  }

  get hasNext(): boolean {
    return this.resolveCurrentIndex() < this._items.length - 1;
  }

  get hasPrev(): boolean {
    return this.resolveCurrentIndex() > 0;
  }

  moveNext(): void {
    if (!this.hasNext) return;
    this.setCurrent(this.resolveCurrentIndex() + 1);
    this.emitChanged();
  }

  movePrev(): void {
    if (!this.hasPrev) return;
    this.setCurrent(this.resolveCurrentIndex() - 1);
    this.emitChanged();
  }

  moveInto(key: NavigationItemKey): void {
    const index = this._items.findIndex((i) => matchesKey(i, key));
    if (index === -1) {
      log.warn(`[SuggestionNavigationService.moveInto] key not found in items: ${key.type}`);
      return;
    }

    this.setCurrent(index);
    this.emitChanged();
  }

  on<K extends keyof SuggestionNavigationEvents>(
    event: K,
    listener: SuggestionNavigationEvents[K],
  ): Unsubscribe {
    return this._emitter.on(event, listener);
  }

  /** Resolves the current index from the key, falling back to last known index clamped to bounds. */
  private resolveCurrentIndex(): number {
    if (this._currentKey) {
      const index = this._items.findIndex((i) => matchesKey(i, this._currentKey!));
      if (index !== -1) {
        this._lastKnownIndex = index;
        return index;
      }
    }

    // Key not found — clamp last known index to valid range.
    if (this._items.length === 0) return -1;
    return Math.min(this._lastKnownIndex, this._items.length - 1);
  }

  /** Updates both the key and last known index to point at the given index. */
  private setCurrent(index: number): void {
    const item = this._items[index];
    this._currentKey = item ? toKey(item) : undefined;
    this._lastKnownIndex = index;
  }

  private emitChanged(): void {
    const item = this._items[this.resolveCurrentIndex()];
    if (!item) {
      log.warn(
        `[SuggestionNavigationService.emitChanged] no item at resolved index, skipping emit`,
      );
      return;
    }
    this._emitter.emit('navigationItemChanged', { item });
  }

  private scheduleRebuild(): void {
    if (this._rebuildTimer !== undefined) return;
    this._rebuildTimer = setTimeout(() => {
      this._rebuildTimer = undefined;
      this.rebuild();
    }, SuggestionNavigationService.REBUILD_THROTTLE_MS);
  }

  private rebuild(): void {
    if (!this._content) return;

    const items: NavigationItem[] = [];
    const sectionOrder = collectDocIds(this._content);

    for (const docId of sectionOrder) {
      // When we reach the epics section, insert epic/task navigation items.
      if (docId.type === 'section' && docId.sectionId === SpecialSectionId.Epics) {
        this.appendEpicItems(items);
        continue;
      }

      const key = stringifyDocId(docId);
      const editReport = this._sectionReports.get(key);
      const commentReport = this._commentReports.get(key);
      if (!editReport && !commentReport) continue;

      const documentId = editReport?.documentId ?? commentReport!.documentId;

      // Collect edit items with their position for interleaving
      const editItems: { sortingOrder: number; item: NavigationItem }[] = [];
      if (editReport) {
        for (const edit of editReport.changes) {
          for (const change of edit.changes) {
            editItems.push({
              sortingOrder: change.fromPos,
              item: {
                type: 'doc_inline_edit',
                documentId,
                inlineEditId: edit.inlineEditId,
                change,
              },
            });
          }
        }
      }

      // Collect comment items with their position for interleaving
      const commentItems: { sortingOrder: number; item: NavigationItem }[] = [];
      if (commentReport) {
        for (const comment of commentReport.comments) {
          commentItems.push({
            sortingOrder: comment.sortingOrder,
            item: { type: 'doc_inline_comment', documentId, commentId: comment.commentId },
          });
        }
      }

      // Merge both lists sorted by document position
      const merged = [...editItems, ...commentItems].sort(
        (a, b) => a.sortingOrder - b.sortingOrder,
      );
      for (const entry of merged) {
        items.push(entry.item);
      }
    }

    this._items = items;

    log.debug(
      { items: items.map((i) => toKey(i)) },
      `[SuggestionNavigationService.rebuild] ${items.length} navigation items`,
    );

    this._emitter.emit('navigationStateReset');
  }

  private appendEpicItems(items: NavigationItem[]): void {
    if (!this._content) return;

    const epics = listEpics(this._content);
    for (const epic of epics) {
      if (this._epicContainerReports.has(`${epic.id}:title`)) {
        items.push({ type: 'epic_title_container', epicId: epic.id });
      }
      if (this._epicContainerReports.has(`${epic.id}:description`)) {
        items.push({ type: 'epic_description_container', epicId: epic.id });
      }

      for (const task of epic.tasks) {
        if (this._taskContainerReports.has(`${task.id}:title`)) {
          items.push({ type: 'task_title_container', taskId: task.id, epicId: epic.id });
        }
        if (this._taskContainerReports.has(`${task.id}:description`)) {
          items.push({ type: 'task_description_container', taskId: task.id, epicId: epic.id });
        }
      }
    }
  }
}
